// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CalculatedFieldStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import fetcher from 'fetchers/Izenda.DataModelDataFetcher';
import reportDesignerFetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import CalculatedFieldModel from './models/Izenda.CalculatedField';
import ExpressionSelection from 'logic/models/Izenda.ExpressionSelection';
import ExpressionFieldName from 'logic/models/Izenda.ExpressionFieldName';
import ExpressionFunctionOperator from 'logic/models/Izenda.ExpressionFunctionOperator';
import constants from 'logic/data/Izenda.CalculatedFieldConstants';
import commonDataStore from 'logic/Izenda.ReportDesignerCommonDataStore';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
import DateTimeUtils from 'share/utils/Izenda.DateTimeUtils';
import objectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import numericAndMoneyFormatConstants from 'logic/data/Izenda.NumericAndMoneyFormatConstants';
import { action, observable } from 'mobx';
import { isNil, isEmpty, isEqual, has, includes, cloneDeep, get, find, remove, defaultTo, invokeArgs } from 'lodash/fp';
import reportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';

/**
 * Izenda Performace View Model
 */
export default class CalculatedFieldStore {
  @observable model: CalculatedFieldModel = null;
  @observable clonedModel: CalculatedFieldModel = null;
  @observable expressionSelection: ExpressionSelection = null;
  reportKey: any = null;

  constructor() {
    this.init();
    this.expressionSelection = new ExpressionSelection();
    this.reportKey = null;
  }

  /**
   * init for the first time
   */
  @action
  init(): void {
    this.model = new CalculatedFieldModel();
    this.clonedModel = this.model.clone();
  }

  /**
   * Preview result
   * @param {any} querySourceId
   */
  preview(querySourceId: any): any {
    const clonedModel = this.clonedModel;
    const reportKey = this.reportKey;

    const { expression, izendaDataType, id: querySourceFieldId, previewRecord } = clonedModel;

    if (reportKey) {
      const data = {
        reportKey: {
          key: reportKey
        },
        expression,
        izendaDataType,
        querySourceFieldId,
        previewRecord
      };

      return reportDesignerFetcher.previewDSCalculatedField(data).then(clonedModel.readPreviewResult.bind(clonedModel));
    }

    return fetcher
      .previewCalculatedField(querySourceId, expression, izendaDataType, querySourceFieldId)
      .then(clonedModel.readPreviewResult.bind(clonedModel));
  }

  /**
   * Preview Result in Sub Total/Grand Total
   * @param {any} reportDesigner
   * @param {any} reportPart
   */
  previewReportProperties(reportDesigner: any, reportPart: any): any {
    const clonedModel = this.clonedModel;
    const { type } = clonedModel;

    if (!reportDesigner && !reportPart) {
      return Promise.resolve();
    }

    const configField = this.getConfigFieldInReportDefinition(reportDesigner.getReportDefinition().report, reportPart);
    const calculatedDetail = this.getCalculatedDetail(reportDesigner, reportPart);

    switch (type.name) {
      case constants.TYPES.GRAND_TOTAL.name:
        return reportDesignerFetcher.previewGrandTotal(calculatedDetail).then(result => {
          this.revertToBackupData(configField);
          return clonedModel.readPreviewResult(result);
        });
      case constants.TYPES.SUB_TOTAL.name:
        return reportDesignerFetcher.previewSubTotal(calculatedDetail).then(result => {
          this.revertToBackupData(configField);
          return clonedModel.readPreviewResult(result);
        });
      default:
        return Promise.resolve();
    }
  }

  /**
   * revert config field to backup data
   * @param {any} configField
   */
  @action
  revertToBackupData(configField: any): void {
    const backupSubTotal = cloneDeep(get('properties.dataFormattings.subTotal', configField));
    const backupGrandTotal = cloneDeep(get('properties.dataFormattings.grandTotal', configField));

    if (configField) {
      const { properties } = configField;
      properties.updateSubTotalData(backupSubTotal);
      properties.updateGrandTotalData(backupGrandTotal);
    }
  }

  /**
   * Validate Expression
   * @param {any} querySourceId
   * @param {boolean} noUpdateDataType
   */
  validateExpression(querySourceId: any, noUpdateDataType: boolean): any {
    const { querySourceId: modelQuerySourceId, expression, izendaDataType, id } = this.clonedModel;
    if (this.reportKey) {
      return this.validateFieldExpression().then(result => this.readDataType(result, noUpdateDataType));
    }
    return fetcher
      .validateExpression(querySourceId || modelQuerySourceId, expression, izendaDataType, id)
      .then(result => this.readDataType(result, noUpdateDataType));
  }

  /**
   * Read data type
   * @param {any} result
   * @param {boolean} noUpdateDataType
   */
  @action
  readDataType(data: any, noUpdateDataType: boolean): any {
    let model = this.clonedModel;

    if (!noUpdateDataType) {
      return model.readDataType(data);
    }
    const { result, messages } = data;
    if (result) {
      model.suggestedDataType = defaultTo(null, result.izendaDataType);
      model.validExpression = isEmpty(messages);
      model.isRunningField = defaultTo(false, result.isRunningField);
      model.isCompositeField = defaultTo(false, result.isCompositeField);
      if (isNil(model.izendaDataType)) {
        model.izendaDataType = defaultTo(null, result.izendaDataType);
      }
      return messages;
    } else {
      return messages;
    }
  }

  /**
   * Validate field expression with report key
   * Validate for Data Model, Add/Edit Calculated Field
   */
  validateFieldExpression(): any {
    const { expression, izendaDataType, id: querySourceFieldId, previewRecord } = this.clonedModel;
    const validateDetail = {
      reportKey: {
        key: this.reportKey
      },
      expression,
      izendaDataType,
      querySourceFieldId,
      previewRecord
    };

    return reportDesignerFetcher.validateFieldExpression(validateDetail);
  }

  /**
   * Validate expression with report key for sub total or grand total
   * @param {any} reportDesigner
   * @param {Object} reportPart
   * @param {boolean} noUpdateDataType
   */
  validateExpressionWithReportKey(reportDesigner: any, reportPart: any, noUpdateDataType: boolean): any {
    if (!reportDesigner && !reportPart) {
      return Promise.resolve();
    }
   
    const { type } = this.clonedModel;
    const configField = this.getConfigFieldInReportDefinition(reportDesigner.getReportDefinition().report, reportPart);
    const validateDetail = this.getCalculatedDetail(reportDesigner, reportPart);

    if (isEqual(type, constants.TYPES.GRAND_TOTAL)) {
      return reportDesignerFetcher
        .validateGrandTotalExpression(validateDetail)
        .then(data => this.updateDataType(configField, data, noUpdateDataType));
    } else if (isEqual(type, constants.TYPES.SUB_TOTAL)) {
      return reportDesignerFetcher
        .validateSubTotalExpression(validateDetail)
        .then(data => this.updateDataType(configField, data, noUpdateDataType));
    } else {
      return Promise.resolve();
    }
  }

  /**
   * update data type
   * @param {any} configField
   * @param {any} data
   * @param {boolean} noUpdateDataType
   */
  @action
  updateDataType(configField: any, data: any, noUpdateDataType: boolean): any {
    const model = this.clonedModel;
    this.revertToBackupData(configField);
    !noUpdateDataType && model.readDataType(data);
    return data.messages;
  }

  /**
   * Fill calculated field data to store, usually call this method when a new calculated field added
   * @param selectedDataSource
   * @param dataSource
   * @param functionOperators
   * @param reportKey
   */
  @action
  fillCalculatedFieldData(
    selectedDataSource: any,
    dataSource: any,
    functionOperators: any,
    reportKey: any,
    fieldNames: any
  ): void {
    this.reportKey = reportKey;
    this.expressionSelection = new ExpressionSelection({
      expressionFieldName: new ExpressionFieldName({
        fieldNames: fieldNames,
        selectedDataSource: selectedDataSource,
        dataSources: dataSource
      }),
      expressionFunctionOperators: new ExpressionFunctionOperator({
        functionOperators: functionOperators
      })
    });
  }

  /**
   * Get calculated data for validate/preview in Grand Total/Sub Total
   * @param {Object} reportPart
   * @returns {Object}
   */
  getCalculatedDetail(reportDesigner: any, reportPart: any): any {
    const {
      type,
      name,
      functionName,
      izendaDataType,
      previewResult,
      format,
      expression,
      id,
      previewRecord,
      font
    } = this.clonedModel;
    const selectedField = reportPart.configField;
    const reportDefinition = reportDesigner.getReportDefinition();
    const { report } = reportDefinition;
    const { tenantId } = reportDefinition.reportKey;
    const isSubTotal = type.title === constants.TYPES.SUB_TOTAL.title;

    const propertiesData = {
      label: name,
      function: functionName,
      expression: functionName === constants.FUNCTION_NAME.EXPRESSION ? expression : '',
      dataType: izendaDataType,
      previewResult: previewResult,
      format: format,
      font:font
    };

    const configField = this.getConfigFieldInReportDefinition(report, reportPart);

    // update new subtotal/grandtotal set on popup
    if (configField) {
      const { properties } = configField;
      isSubTotal ? properties.updateSubTotalData(propertiesData) : properties.updateGrandTotalData(propertiesData);

      if (reportPart.type === reportDesignerConstants.REPORT_PART_TYPE.Form && isSubTotal) {
        const subTotal = invokeArgs('getDataValueByKey', ['subTotal'], properties);
        if (!!subTotal.label) {
          subTotal.levels = [''];
          invokeArgs('setDataFormatByKey', ['subTotal', subTotal], properties);
        }
      }
    }

    return {
      reportKey: {
        key: this.reportKey,
        tenantId: tenantId
      },
      report: report,
      title: reportPart.headerTitle,
      expression: expression,
      totalProperties: propertiesData,
      izendaDataType: izendaDataType,
      reportField: selectedField.field,
      querySourceFieldId: id,
      previewRecord: previewRecord,
      font: font
    };
  }

  /**
   * Get config field in report definition
   * @param {Object} reportDefinition
   * @param {Object} reportPart
   */
  getConfigFieldInReportDefinition(reportDefinition: any, reportPart: any): any {
    // Set information for field in report part
    const filteredReportPart = find(
      item => item.state !== objectStateConstants.DELETE && item.title === reportPart.headerTitle,
      reportDefinition.reportPart
    );
    const { reportPartContent } = filteredReportPart;

    const compareField = (element, reportPart) => {
      return (
        get('field.fieldId', element) === get('configField.field.fieldId', reportPart) &&
        get('name', element) === get('configField.name', reportPart)
      );
    };
    // Lookup field in columns container
    let configField = find(ele => compareField(ele, filteredReportPart), get('columns.elements', reportPartContent));
    // Lookup field in rows container
    if (!configField) {
      configField = find(ele => compareField(ele, filteredReportPart), get('rows.elements', reportPartContent));
    }
    // Lookup field in values container (in case of pivot/drilldown grid)
    if (!configField) {
      configField = find(ele => compareField(ele, filteredReportPart), get('values.elements', reportPartContent));
    }
    // Lookup field in seperator container
    if (!configField) {
      configField = find(ele => compareField(ele, filteredReportPart), get('separators.elements', reportPartContent));
    }

    return configField;
  }

  /**
   * Get format by type
   * @param {any} type
   */
  @action
  getFormatsByType(type: any): any {
    let model = this.clonedModel;
    return commonDataStore.loadReportFieldDataFormat(type).then(formats => {
      // filter date time format that current user setting can supported
      if (model.izendaDataType === DATA_TYPES.DATE_TIME) {
        formats = DateTimeUtils.filterDateTimeFormat(formats);
      }

      // Remove format for subtotal or grandtotal popup
      if (model.izendaDataType === DATA_TYPES.NUMERIC || model.izendaDataType === DATA_TYPES.MONEY) {
        formats = remove(
          format =>
            // Remove format only on field grid pivot
            ((has('subTotalProperties', model.type) || has('grandTotalProperties', model.type)) &&
              includes(format.name, numericAndMoneyFormatConstants.FORMATS_ONLY_ON_PIVOT_GRID_VALUE_CONTAINER)) ||
            // Remove subtotal format from grid separator and grand total popup
            ((has('grandTotalProperties', model.type) || !model.canSetSubtotal) &&
              includes(format.name, numericAndMoneyFormatConstants.FORMAT_NOT_AVAILABLE_ON_FIELD_CAN_NOT_SET_SUBTOTAL)),
          formats
        );
      }
      model.formats = formats;
      return formats;
    });
  }

  /**
   * clone model
   */
  acceptClonedModel(): void {
    const data = this.clonedModel;
    this.model.populateData(data);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.CalculatedFieldStore.js